import { useEffect, useState } from "react";
import { api, apiError, formatINR, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { STATUS } from "@/components/AppointmentTable";
import { toast } from "sonner";
import { CalendarCheck, IndianRupee, CheckCircle2, Inbox, PlusCircle, XCircle, Download } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const Stat = ({ icon: Icon, label, value, tint, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-5 w-5" /></div>
    <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="font-head text-2xl font-bold text-slate-900" data-testid={testid}>{value}</div></div>
  </div>
);

export default function DaySummary() {
  const [date, setDate] = useState(todayStr);
  const [data, setData] = useState(null);

  useEffect(() => {
    let stale = false;
    api.get("/reports/day-summary", { params: { date } }).then((r) => { if (!stale) setData(r.data); }).catch((e) => toast.error(apiError(e)));
    return () => { stale = true; };
  }, [date]);

  return (
    <div className="space-y-6" data-testid="day-summary">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600">Shift date</label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 bg-white" data-testid="summary-date-input" />
        <button onClick={() => setDate(todayStr())} className="text-sm text-teal-700 hover:underline">Today</button>
        <Button variant="outline" className="ml-auto" data-testid="export-summary-button"
          onClick={() => downloadCsv("/reports/day-summary/export", { date }, "day-summary.csv").catch((e) => toast.error(apiError(e)))}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {data && (
        <>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Stat icon={IndianRupee} label="Cash Collected" value={formatINR(data.cash_collected)} tint="bg-emerald-50 text-emerald-600" testid="summary-cash" />
            <Stat icon={CalendarCheck} label="Visits Scheduled" value={data.scheduled} tint="bg-teal-50 text-teal-600" testid="summary-scheduled" />
            <Stat icon={CheckCircle2} label="Completed" value={data.completed} tint="bg-blue-50 text-blue-600" testid="summary-completed" />
            <Stat icon={PlusCircle} label="Booked Today" value={data.booked_today} tint="bg-amber-50 text-amber-600" testid="summary-booked" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3"><Inbox className="h-4 w-4 text-slate-400" /> New booking requests received: <b>{data.requests_today}</b></div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3"><XCircle className="h-4 w-4 text-slate-400" /> Cancelled visits: <b>{data.cancelled}</b></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-head font-semibold mb-3">Collection by Doctor</h3>
              {data.by_doctor.length === 0 ? <div className="text-slate-400 text-sm py-6 text-center">No visits on this day.</div> : (
                <div className="divide-y" data-testid="summary-by-doctor">
                  {data.by_doctor.map((d) => (
                    <div key={d.doctor_id} className="flex items-center justify-between py-3">
                      <div><div className="font-medium text-slate-900">{d.doctor_name}</div><div className="text-xs text-slate-500">{d.patients} patient{d.patients === 1 ? "" : "s"}</div></div>
                      <div className="font-mono font-semibold text-teal-700">{formatINR(d.collection)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-head font-semibold mb-3">Visits on {new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</h3>
              {data.appointments.length === 0 ? <div className="text-slate-400 text-sm py-6 text-center">Nothing scheduled.</div> : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {data.appointments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 text-sm border border-slate-100 rounded-lg px-3 py-2">
                      <span className="font-mono text-xs text-slate-500 w-14">{new Date(a.appointment_date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="flex-1 truncate"><b>{a.patient_name}</b> · {a.doctor_name}</span>
                      <span className="font-mono">{formatINR(a.amount)}</span>
                      <Badge variant="outline" className={`capitalize ${STATUS[a.status] || ""}`}>{a.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
